import PropTypes from 'prop-types';
import { Chip, ChipSizes, ChipVariants } from '@leegality/leegality-react-component-library';
import { Icon, IconColors, IconKeys } from '@components/icon';
import { GrantStatus } from '@constants';
import { CHANNEL_ICONS, CHANNEL_ORDER, isCpLocked } from './channel-utils';

// Per-purpose chip strip. Each chip toggles between ENABLED (brand color) and
// DISABLED (gray). Mandatory channels stay ENABLED + lock and ignore clicks.
// Reuses `consent-sdk-channel-chips` so the library Chip's --primary-* tokens
// resolve to the tenant brand color.
const PurposeChannelChips = ({
  brandColor, preferences, channelState, onToggle, visibleField,
}) => {
  const visiblePreferences = (preferences || []).filter((pref) => pref?.[visibleField]);
  const channelIds = CHANNEL_ORDER.filter(
    (id) => visiblePreferences.some((pref) => pref?.channel === id),
  );

  if (channelIds.length === 0) return null;

  return (
    <div
      className="consent-sdk-channel-chips flex gap-1.5 mt-1.5"
      style={{ '--brand-color': brandColor }}
    >
      {channelIds.map((channelId) => {
        const iconName = CHANNEL_ICONS[channelId];
        if (!iconName) return null;
        const cp = visiblePreferences.find((pref) => pref.channel === channelId);
        const isLocked = isCpLocked(cp, visibleField);
        const isEnabled = isLocked || channelState?.[channelId] === GrantStatus.GRANTED;
        const iconColor = isEnabled ? brandColor : IconColors.GRAY;
        return (
          <span key={channelId} className={`relative inline-flex${isLocked ? ' locked-chip' : ''}`}>
            <Chip
              variant={ChipVariants.OUTLINED}
              size={ChipSizes.SMALL}
              selected={isEnabled}
              disabled={isLocked}
              onClick={isLocked ? undefined : () => onToggle(channelId)}
              renderIcon={() => <Icon iconName={iconName} size={12} color={iconColor} />}
            />
            {isLocked && (
              <span
                className="absolute -top-1 -right-1 inline-flex items-center justify-center w-3 h-3 rounded-full bg-gray-100 pointer-events-none"
              >
                <Icon iconName={IconKeys.Lock} size={8} color={IconColors.GRAY} />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

PurposeChannelChips.propTypes = {
  brandColor: PropTypes.string.isRequired,
  preferences: PropTypes.arrayOf(PropTypes.shape({
    channel: PropTypes.string,
    mandatory: PropTypes.bool,
    preSelected: PropTypes.bool,
  })),
  channelState: PropTypes.objectOf(PropTypes.oneOf(Object.values(GrantStatus))),
  onToggle: PropTypes.func,
  visibleField: PropTypes.string,
};

PurposeChannelChips.defaultProps = {
  preferences: [],
  channelState: {},
  onToggle: () => {},
  visibleField: 'showInNotice',
};

export default PurposeChannelChips;
